import * as MicrosoftTeams from '@microsoft/teams-js';
import { from, Observable, ReplaySubject } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { IUserLoginInfo } from '../models/i-user-login-info';
import { TeamsContextResolver } from '../common/context-resolvers/teams-context.resolver';
import { IMicrosoftTeamsContextAccessor } from '../contract/i-microsoft-teams-context.accessor';
import { AllClientTypes } from '../models/client-type';
import { ITeamsContext } from '../models/i-teams.context';
import { ITeamContext } from '../common/context-resolvers/i-team.context';

@Injectable()
export class MicrosoftTeamsContextAcessor implements IMicrosoftTeamsContextAccessor {
  private readonly nativeContextInfo: ReplaySubject<ITeamsContext>;
  private readonly nativeThemeInfo: ReplaySubject<string>;
  private readonly nativeHostClientInfo: ReplaySubject<AllClientTypes>;
  private hasTeamInOriginalContext: boolean = false;

  private currentContext: ITeamsContext;
  private context$: Observable<ITeamsContext>;
  private loginHint$: Observable<string>;
  private clientInfo$: Observable<AllClientTypes>;
  private themeInformation$: Observable<string>;

  public constructor(private readonly teamsContextResolver: TeamsContextResolver) {
    this.nativeContextInfo = new ReplaySubject<ITeamsContext>(1);
    this.nativeThemeInfo = new ReplaySubject<string>(1);
    this.nativeHostClientInfo = new ReplaySubject<AllClientTypes>();

    from(this.initialize()).pipe(take(1)).subscribe();
  }

  private async initialize() {
    if (!MicrosoftTeams.app.isInitialized()) {
      await MicrosoftTeams.app.initialize();
    }

    MicrosoftTeams.app.registerOnThemeChangeHandler((theme) => {
      this.nativeThemeInfo.next(theme);
    });

    const msTeamsContext = await MicrosoftTeams.app.getContext();

    const context: ITeamsContext = msTeamsContext.team
      ? {
          tenantId: msTeamsContext.user.tenant.id,
          teamId: msTeamsContext.team.internalId,
          teamName: msTeamsContext.team.displayName,
          groupId: msTeamsContext.team.groupId,
          conversationId: msTeamsContext.channel.id,
          userObjectId: msTeamsContext.user.id,
          loginHint: msTeamsContext.user.loginHint,
          subEntityId: msTeamsContext.page.subPageId,
        }
      : {
          tenantId: msTeamsContext.user.tenant.id,
          groupId: null,
          userObjectId: msTeamsContext.user.id,
          loginHint: msTeamsContext.user.loginHint,
          subEntityId: msTeamsContext.page.subPageId,
        };

    this.hasTeamInOriginalContext = !!msTeamsContext.team && !!msTeamsContext.team.groupId;

    this.teamsContextResolver.updateContext(
      !this.isStaticTab() ? context : this.composeContext(context, this.teamsContextResolver.teamContext),
      msTeamsContext.app.host.clientType,
    );

    this.currentContext = context;
    this.nativeContextInfo.next(context);
    this.nativeThemeInfo.next(msTeamsContext.app.theme);
    this.nativeHostClientInfo.next(msTeamsContext.app.host.clientType);
  }

  public get snapshot(): ITeamsContext {
    return !this.isStaticTab() ? this.currentContext : this.composeContext(this.currentContext, this.teamsContextResolver.teamContext);
  }

  public getContext$(): Observable<ITeamsContext> {
    return (this.context$ || (this.context$ = this.nativeContextInfo.asObservable())).pipe(
      map((context) => (!this.isStaticTab() ? context : this.composeContext(context, this.teamsContextResolver.teamContext))),
    );
  }
  public getLoginHint$(): Observable<string> {
    return this.loginHint$ || (this.loginHint$ = this.getContext$().pipe(map((ctx) => ctx.loginHint)));
  }
  public getClientInfo$(): Observable<AllClientTypes> {
    return this.clientInfo$ || (this.clientInfo$ = this.nativeHostClientInfo.asObservable());
  }
  public getThemeInformation$(): Observable<string> {
    return this.themeInformation$ || (this.themeInformation$ = this.nativeThemeInfo.asObservable());
  }

  public isStandAlone(): boolean {
    return this.teamsContextResolver.isStandAlone;
  }

  public isStaticTab(): boolean {
    return !this.hasTeamInOriginalContext;
  }

  public refreshContext(): void {
    if (this.isStaticTab()) {
      this.teamsContextResolver.refreshContext();
    }
  }

  public trySpecifyUser(user: IUserLoginInfo): ITeamsContext {
    return this.snapshot;
  }

  private composeContext(context: ITeamsContext, resolvedContext: ITeamContext) {
    return { ...context, ...resolvedContext };
  }
}
